import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import DaxLiveHeader from "@/components/DaxLiveHeader";
import {
  ShieldCheck,
  ShieldWarning,
  Siren,
  Clock,
  Bell,
  CalendarBlank,
  Play,
  Pause,
  FloppyDisk,
  ArrowCounterClockwise,
  CurrencyEur,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const COLORS = { profit: "#1D9E75", loss: "#D85A30", neutral: "#378ADD", warn: "#D97706" };
const BOT_META = {
  scalp: { label: "Scalp", color: "#FF7F6E" },
  daytrade: { label: "Day Trade", color: "#378ADD" },
  swing: { label: "Swing", color: "#14B8A6" },
};

// Mock economic news calendar (hardcoded weekly-recurring events)
const NEWS_CALENDAR = [
  { day: "Lundi", time: "10:00", label: "DE ZEW Economic Sentiment", severity: "medium" },
  { day: "Mardi", time: "08:00", label: "DE CPI (Inflation)", severity: "high" },
  { day: "Mercredi", time: "14:00", label: "ECB Interest Rate Decision", severity: "high" },
  { day: "Jeudi", time: "14:30", label: "US NFP / Unemployment Claims", severity: "high" },
  { day: "Vendredi", time: "14:30", label: "US GDP / CPI", severity: "high" },
  { day: "Vendredi", time: "11:00", label: "EU Industrial Production", severity: "medium" },
];

const severityColor = (sev) =>
  sev === "high" ? COLORS.loss : sev === "medium" ? COLORS.warn : COLORS.neutral;

export default function RulesAlerts() {
  const [rules, setRules] = useState(null);
  const [status, setStatus] = useState(null);
  const [riskEvents, setRiskEvents] = useState([]);
  const [botConfigs, setBotConfigs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [r, s, e, b] = await Promise.all([
        fetch(`${API}/risk-rules`).then((x) => x.json()),
        fetch(`${API}/risk-rules/check`).then((x) => x.json()),
        fetch(`${API}/bots/risk-events?limit=10`).then((x) => x.json()),
        fetch(`${API}/bots/configs`).then((x) => x.json()),
      ]);
      setRules(r);
      setStatus(s);
      setRiskEvents(Array.isArray(e) ? e : []);
      setBotConfigs(Array.isArray(b) ? b : []);
    } catch (err) {
      toast.error("Erreur chargement règles");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const updateLimit = (field, value) => {
    setRules((prev) => ({ ...prev, limits: { ...prev.limits, [field]: value } }));
  };
  const updateCapital = (field, value) => {
    setRules((prev) => ({ ...prev, capital: { ...prev.capital, [field]: value } }));
  };
  const updateSession = (field, value) => {
    setRules((prev) => ({ ...prev, session: { ...prev.session, [field]: value } }));
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/risk-rules`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rules),
      });
      if (!res.ok) throw new Error("Save failed");
      toast.success("Règles sauvegardées");
      fetchAll();
    } catch (err) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const resetRules = () => {
    if (!window.confirm("Réinitialiser toutes les règles aux valeurs par défaut ?")) return;
    setRules({
      id: "main",
      capital: { scalp_eur: 1000, daytrade_eur: 1800, swing_eur: 800, safety_reserve_eur: 400, total_eur: 4000 },
      limits: { max_daily_loss_pct: 3, max_weekly_loss_pct: 6, max_drawdown_pct: 12, scalp_consecutive_loss_limit: 3 },
      session: { avoid_news_minutes: 30, blackout_events: ["ECB", "NFP", "CPI", "GDP"], monday_scalp_min_time: "08:30", friday_close_all_before: "11:30", bot_priority: ["swing", "daytrade", "scalp"] },
      targets: { monthly_pnl_min_pct: 3, monthly_pnl_max_pct: 6, annual_pnl_min_pct: 36, annual_pnl_max_pct: 72, target_win_rate_min: 50, target_win_rate_max: 58, target_avg_rr: 2 },
    });
    toast.info("Valeurs par défaut chargées — n'oubliez pas de sauvegarder");
  };

  const toggleBot = async (botId) => {
    try {
      await fetch(`${API}/bots/toggle/${botId}`, { method: "POST" });
      fetchAll();
    } catch (err) {
      toast.error("Erreur toggle bot");
    }
  };

  const updateBotHours = async (botId, field, value) => {
    const cfg = botConfigs.find((c) => c.bot_id === botId);
    if (!cfg) return;
    await fetch(`${API}/bots/configs/${botId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...cfg, [field]: value }),
    });
    toast.success(`${BOT_META[botId].label} : ${field} = ${value}`);
    fetchAll();
  };

  if (loading || !rules) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-slate-100 rounded" />
          <div className="h-40 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  const recoBadge = {
    OK: { text: "Tout va bien", color: "bg-[#1D9E75]/15 text-[#1D9E75] border-[#1D9E75]/40", Icon: ShieldCheck },
    PAUSE_SCALP: { text: "Scalp en pause recommandée", color: "bg-[#D97706]/15 text-[#D97706] border-[#D97706]/40", Icon: ShieldWarning },
    PAUSE_ALL: { text: "Tous bots en pause — seuil dépassé", color: "bg-[#D85A30]/15 text-[#D85A30] border-[#D85A30]/40", Icon: Siren },
  }[status?.recommendation || "OK"];

  const RecoIcon = recoBadge.Icon;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6" data-testid="rules-alerts-page">
      <DaxLiveHeader />

      {/* ==== HEADER ==== */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading" data-testid="page-title">
            Règles & Alertes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configuration du risque global, horaires des bots, et calendrier économique.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={resetRules}
            data-testid="reset-rules-btn"
            className="rounded-sm"
          >
            <ArrowCounterClockwise size={16} className="mr-2" /> Réinitialiser
          </Button>
          <Button
            onClick={saveRules}
            disabled={saving}
            data-testid="save-rules-btn"
            className="bg-[#1D9E75] hover:bg-[#167a5a] text-white rounded-sm"
          >
            <FloppyDisk size={16} className="mr-2" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>
      </div>

      {/* ==== LIVE RISK STATUS ==== */}
      <Card className={`rounded-sm border ${recoBadge.color.replace("text-", "border-").split(" ")[0]}`}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <RecoIcon size={20} />
            Statut Risque Temps Réel
            <Badge className={`${recoBadge.color} border ml-2`} data-testid="risk-reco-badge">
              {recoBadge.text}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div data-testid="daily-dd-card">
              <div className="text-xs text-slate-500">Drawdown journalier</div>
              <div
                className="text-2xl font-bold"
                style={{ color: status?.flags?.daily_loss_breach ? COLORS.loss : COLORS.neutral }}
              >
                {status?.daily_dd_pct?.toFixed(2)}%
              </div>
              <div className="text-xs text-slate-400">
                {status?.daily_pnl_eur?.toFixed(2)}€ / limite {rules.limits.max_daily_loss_pct}%
              </div>
            </div>
            <div data-testid="weekly-dd-card">
              <div className="text-xs text-slate-500">Drawdown hebdo</div>
              <div
                className="text-2xl font-bold"
                style={{ color: status?.flags?.weekly_loss_breach ? COLORS.loss : COLORS.neutral }}
              >
                {status?.weekly_dd_pct?.toFixed(2)}%
              </div>
              <div className="text-xs text-slate-400">
                {status?.weekly_pnl_eur?.toFixed(2)}€ / limite {rules.limits.max_weekly_loss_pct}%
              </div>
            </div>
            <div data-testid="scalp-streak-card">
              <div className="text-xs text-slate-500">Pertes consécutives Scalp</div>
              <div
                className="text-2xl font-bold"
                style={{ color: status?.flags?.scalp_streak_breach ? COLORS.loss : COLORS.neutral }}
              >
                {status?.scalp_consecutive_losses ?? 0}
              </div>
              <div className="text-xs text-slate-400">
                limite : {rules.limits.scalp_consecutive_loss_limit}
              </div>
            </div>
            <div data-testid="capital-card">
              <div className="text-xs text-slate-500">Capital total</div>
              <div className="text-2xl font-bold text-slate-900">
                {status?.capital?.toLocaleString("fr-FR")} €
              </div>
              <div className="text-xs text-slate-400">
                répartition active
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ==== RISK SLIDERS ==== */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck size={18} /> Limites de Risque
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <SliderRow
            testid="slider-daily-loss"
            label="Perte journalière max (%)"
            value={rules.limits.max_daily_loss_pct}
            onChange={(v) => updateLimit("max_daily_loss_pct", v)}
            min={1}
            max={10}
            step={0.5}
            unit="%"
            capital={rules.capital.total_eur}
            showEur
          />
          <SliderRow
            testid="slider-weekly-loss"
            label="Perte hebdo max (%)"
            value={rules.limits.max_weekly_loss_pct}
            onChange={(v) => updateLimit("max_weekly_loss_pct", v)}
            min={2}
            max={20}
            step={0.5}
            unit="%"
            capital={rules.capital.total_eur}
            showEur
          />
          <SliderRow
            testid="slider-max-dd"
            label="Drawdown max historique (%)"
            value={rules.limits.max_drawdown_pct}
            onChange={(v) => updateLimit("max_drawdown_pct", v)}
            min={5}
            max={30}
            step={1}
            unit="%"
            capital={rules.capital.total_eur}
            showEur
          />
          <SliderRow
            testid="slider-scalp-streak"
            label="Pertes consécutives Scalp max"
            value={rules.limits.scalp_consecutive_loss_limit}
            onChange={(v) => updateLimit("scalp_consecutive_loss_limit", v)}
            min={2}
            max={8}
            step={1}
            unit=" trades"
          />
        </CardContent>
      </Card>

      {/* ==== CAPITAL ALLOCATION ==== */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CurrencyEur size={18} /> Répartition du Capital
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { key: "scalp_eur", label: "Scalp", color: BOT_META.scalp.color },
            { key: "daytrade_eur", label: "Day Trade", color: BOT_META.daytrade.color },
            { key: "swing_eur", label: "Swing", color: BOT_META.swing.color },
            { key: "safety_reserve_eur", label: "Réserve", color: "#64748B" },
            { key: "total_eur", label: "Total", color: "#0F172A" },
          ].map(({ key, label, color }) => (
            <div key={key} data-testid={`capital-${key}`}>
              <Label className="text-xs" style={{ color }}>
                {label}
              </Label>
              <Input
                type="number"
                value={rules.capital[key] || 0}
                onChange={(e) => updateCapital(key, parseFloat(e.target.value) || 0)}
                className="mt-1 rounded-sm"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ==== BOT SCHEDULES ==== */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock size={18} /> Horaires des Bots
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {botConfigs.map((cfg) => {
            const meta = BOT_META[cfg.bot_id] || { label: cfg.bot_id, color: "#64748B" };
            return (
              <div
                key={cfg.bot_id}
                className="flex items-center gap-3 flex-wrap p-3 rounded-sm border border-slate-200 bg-slate-50"
                data-testid={`bot-schedule-${cfg.bot_id}`}
              >
                <div className="flex items-center gap-2 min-w-[140px]">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: meta.color }}
                  />
                  <span className="font-semibold">{meta.label}</span>
                </div>
                <Badge
                  className={
                    cfg.enabled
                      ? "bg-[#1D9E75]/15 text-[#1D9E75] border-[#1D9E75]/40"
                      : "bg-slate-200 text-slate-600 border-slate-300"
                  }
                >
                  {cfg.enabled ? "ACTIF" : "PAUSE"}
                </Badge>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Plage horaire :</Label>
                  <Input
                    defaultValue={cfg.active_hours || ""}
                    onBlur={(e) => {
                      if (e.target.value !== cfg.active_hours) {
                        updateBotHours(cfg.bot_id, "active_hours", e.target.value);
                      }
                    }}
                    className="h-8 w-32 rounded-sm text-sm"
                    placeholder="08:00-10:30"
                    data-testid={`hours-input-${cfg.bot_id}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Max trades/jour :</Label>
                  <Input
                    type="number"
                    defaultValue={cfg.max_trades_per_day ?? ""}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v) && v !== cfg.max_trades_per_day) {
                        updateBotHours(cfg.bot_id, "max_trades_per_day", v);
                      }
                    }}
                    className="h-8 w-20 rounded-sm text-sm"
                  />
                </div>
                <Button
                  size="sm"
                  variant={cfg.enabled ? "outline" : "default"}
                  onClick={() => toggleBot(cfg.bot_id)}
                  className="rounded-sm ml-auto"
                  data-testid={`toggle-${cfg.bot_id}`}
                >
                  {cfg.enabled ? (
                    <>
                      <Pause size={14} className="mr-1" /> Pause
                    </>
                  ) : (
                    <>
                      <Play size={14} className="mr-1" /> Activer
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* ==== SESSION RULES ==== */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarBlank size={18} /> Règles de Session
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Blackout news (minutes avant/après)</Label>
            <Input
              type="number"
              value={rules.session.avoid_news_minutes}
              onChange={(e) => updateSession("avoid_news_minutes", parseInt(e.target.value) || 0)}
              className="mt-1 rounded-sm"
              data-testid="news-minutes"
            />
          </div>
          <div>
            <Label className="text-xs">Scalp lundi — heure min</Label>
            <Input
              value={rules.session.monday_scalp_min_time}
              onChange={(e) => updateSession("monday_scalp_min_time", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="08:30"
              data-testid="monday-min"
            />
          </div>
          <div>
            <Label className="text-xs">Vendredi — clôture avant</Label>
            <Input
              value={rules.session.friday_close_all_before}
              onChange={(e) => updateSession("friday_close_all_before", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="11:30"
              data-testid="friday-close"
            />
          </div>
        </CardContent>
      </Card>

      {/* ==== NEWS CALENDAR ==== */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell size={18} /> Calendrier Économique (hebdomadaire)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {NEWS_CALENDAR.map((n, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm bg-white hover:bg-slate-50 transition-colors"
                data-testid={`news-item-${i}`}
              >
                <span
                  className="w-2 h-10 rounded-sm"
                  style={{ backgroundColor: severityColor(n.severity) }}
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{n.label}</div>
                  <div className="text-xs text-slate-500">
                    {n.day} à {n.time} CET
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="text-xs"
                  style={{ color: severityColor(n.severity), borderColor: severityColor(n.severity) }}
                >
                  {n.severity === "high" ? "Impact fort" : "Impact moyen"}
                </Badge>
              </div>
            ))}
          </div>
          <Alert className="mt-4 border-[#378ADD]/30 bg-[#378ADD]/5">
            <Bell className="h-4 w-4" />
            <AlertTitle>Blackout automatique</AlertTitle>
            <AlertDescription>
              Les bots évitent automatiquement les {rules.session.avoid_news_minutes} minutes autour
              de ces événements (ECB, NFP, CPI, GDP).
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* ==== RISK EVENTS HISTORY ==== */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Siren size={18} /> Historique des mises en pause auto
          </CardTitle>
        </CardHeader>
        <CardContent>
          {riskEvents.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6" data-testid="no-risk-events">
              Aucun événement — le système n'a jamais déclenché de pause automatique.
            </div>
          ) : (
            <div className="space-y-2">
              {riskEvents.map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm bg-[#FEF3F2]"
                  data-testid={`risk-event-${ev.id}`}
                >
                  <Siren size={18} className="text-[#D85A30]" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">
                      {ev.event_type === "PAUSE_ALL" ? "Pause totale" : "Pause Scalp"}
                      {ev.bots_paused?.length > 0 && (
                        <span className="text-slate-500 font-normal ml-2">
                          — bots : {ev.bots_paused.join(", ")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{ev.reason}</div>
                  </div>
                  <div className="text-xs text-slate-400">
                    {ev.created_at ? new Date(ev.created_at).toLocaleString("fr-FR") : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ================== HELPERS ==================

function SliderRow({ testid, label, value, onChange, min, max, step, unit, capital, showEur }) {
  const eur = showEur && capital ? ((value / 100) * capital).toFixed(0) : null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm text-slate-700">{label}</Label>
        <div className="text-right">
          <span className="text-lg font-bold text-slate-900">
            {value}
            {unit}
          </span>
          {eur && <span className="text-xs text-slate-500 ml-2">= {eur}€</span>}
        </div>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
      />
      <div className="flex justify-between text-xs text-slate-400 mt-1">
        <span>
          {min}
          {unit}
        </span>
        <span>
          {max}
          {unit}
        </span>
      </div>
    </div>
  );
}
